import React from "react";
import styled, { keyframes } from "styled-components";

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 24px;
  text-align: center;
  transition: background 0.3s ease;
`;

const SlideLabel = styled.h2`
  color: #1e3a8a;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const AnimatedIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  animation: ${bounce} 2s infinite;
`;

const BadgeTitle = styled.h3<{ $color?: string; $bg?: string }>`
  color: ${p => p.$color || '#2563eb'};
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
  padding: 8px 24px;
  background: ${p => p.$bg || '#eff6ff'};
  border-radius: 20px;
  display: inline-block;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const Description = styled.p`
  color: #475569;
  font-size: 16px;
  line-height: 1.6;
  max-width: 320px;
  margin: 0 auto 32px auto;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  width: 100%;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 16px;
  min-width: 120px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const StatValue = styled.div<{ $color?: string }>`
  font-size: 28px;
  font-weight: 700;
  color: ${p => p.$color || '#1e293b'};
  line-height: 1.2;
`;

const StatSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
`;

interface LoginStatsSlideProps {
  loginCount: number;
  hasScoreHistory?: boolean;
}

const LoginStatsSlide: React.FC<LoginStatsSlideProps> = ({ loginCount, hasScoreHistory = true }) => {
  let descriptionText = "";
  let titleText = "Tu Compromiso";
  let icon = "📊";
  let themeColor = "#2563eb"; // blue-600
  let themeBg = "#dbeafe"; // blue-100 (for badge)
  let gradientEnd = "#abddffff"; // for background

  if (!hasScoreHistory) {
    titleText = "¡Estás cerca!";
    descriptionText = "Tienes un registro activo pero aún no has visto qué hay detrás de tu puntaje. ¡Descubre tu potencial!";
    icon = "🔑";
    themeColor = "#d97706"; // amber-600
    themeBg = "#fef3c7"; // amber-100
    gradientEnd = "#fef3c7";
  } else if (loginCount >= 26) {
    titleText = "¡Qué compromiso!";
    descriptionText = "Estuviste monitoreando activamente tu información. Tu constancia marca la diferencia.";
    icon = "🔥";
    themeColor = "#dc2626"; // red-600
    themeBg = "#fee2e2"; // red-100
    gradientEnd = "#fee2e2";
  } else if (loginCount >= 13) {
    titleText = "Compromiso moderado";
    descriptionText = "Consultaste lo necesario para mantenerte informado. Recuerda que la constancia es clave.";
    icon = "📱";
    themeColor = "#2563eb"; // blue-600
    themeBg = "#dbeafe"; // blue-100
    gradientEnd = "#dbeafe";
  } else {
    titleText = "Buen comienzo";
    descriptionText = "Diste pasos importantes. Para el 2026 puedes aumentar la frecuencia y monitorear tus cambios.";
    icon = "🌱";
    themeColor = "#059669"; // emerald-600
    themeBg = "#d1fae5"; // emerald-100
    gradientEnd = "#d1fae5";
  }

  const averagePerMonth = Math.round(loginCount / 12);

  return (
    <Container style={{ background: `linear-gradient(to top, #ffffff, ${gradientEnd})` }}>
      <SlideLabel>TUS INICIOS DE SESIÓN</SlideLabel>

      <AnimatedIcon>{icon}</AnimatedIcon>

      <BadgeTitle $bg={themeBg} $color={themeColor}>
        {titleText}
      </BadgeTitle>

      <Description>
        {descriptionText}
      </Description>

      <StatsRow>
        <StatCard>
          <StatLabel>Total 2025</StatLabel>
          <StatValue $color={themeColor}>{loginCount}</StatValue>
          <StatSub>Sesiones</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>Promedio</StatLabel>
          <StatValue $color={themeColor}>{averagePerMonth}</StatValue>
          <StatSub>Al mes</StatSub>
        </StatCard>
      </StatsRow>
    </Container>
  );
};

export default LoginStatsSlide;
